<template>
    <div class="main-container">
        <div class="logo-container">
            <GallabLogo class="logo" />
        </div>

        <div class="step-indicator">
            <h1 class="title">WYBIERZ LAWETĘ</h1>
            <div class="steps">
                <div class="step">
                    <p>
                        WYBÓR MATERIAŁU
                    </p>
                </div>
                <div class="step active">
                    <p>
                        WYBÓR MODELU
                    </p>
                </div>
            </div>
        </div>

        <div v-if="selectedMaterial" class="model-selection">
            <div class="selected-material-banner">
                <div class="banner-content">
                    <h2>Wybrano: {{ materialToDisplayName(selectedMaterial) }}</h2>
                    <p>Teraz wybierz model lawety</p>
                    <button @click="goToMaterialSelection" class="change-material">
                        Zmień materiał
                    </button>
                </div>
            </div>

            <div v-if="!hasGroupsForSelectedMaterial" class="empty-state">
                Brak dostępnych modeli dla wybranego materiału
            </div>

            <div v-else>
                <div v-for="(trailers, groupName) in trailerStore.trailersGrouped[selectedMaterial]" :key="groupName"
                    class="model-group">
                    <div class="group-header" @click="toggleGroup(groupName)">
                        <h2>{{ groupName }}</h2>
                        <button class="toggle-button">
                            <font-awesome-icon :icon="isGroupExpanded(groupName) ? 'chevron-up' : 'chevron-down'"
                                size="xs" />
                        </button>
                    </div>
                    <p class="group-description">{{ getGroupDescription(groupName) }}</p>

                    <transition name="fade">
                        <div class="group-container" v-show="isGroupExpanded(groupName)">
                            <ModelItemComponent v-for="trailer in trailers" :key="trailer.id" :trailer="trailer"
                                @click="selectTrailer(trailer)" />
                        </div>
                    </transition>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useTrailerStore } from '@/stores/trailerStore'
import ModelItemComponent from './ModelItemComponent.vue'
import GallabLogo from '@/components/common/GallabLogo.vue'

export default {
    components: {
        ModelItemComponent,
        GallabLogo
    },
    data() {
        return {
            trailerStore: null,
            expandedGroups: new Set()
        }
    },
    created() {
        this.trailerStore = useTrailerStore()
        const material = this.trailerStore.selectedMaterial
        if (material) {
            this.expandedGroups = new Set(
                Object.keys(this.trailerStore.trailersGrouped[material] || {})
            )
        } else {
            this.$router.push('/')
        }
    },
    computed: {
        selectedMaterial() {
            return this.trailerStore.selectedMaterial
        },
        hasGroupsForSelectedMaterial() {
            return (
                this.selectedMaterial &&
                Object.keys(this.trailerStore.trailersGrouped[this.selectedMaterial] || {}).length > 0
            )
        }
    },
    methods: {
        materialToDisplayName(material) {
            return {
                'Stal': 'Laweta Stalowa',
                'Alu': 'Laweta Aluminiowa',
                'Stal/Alu': 'Laweta Stalowa / Aluminiowa'
            }[material] || material
        },
        getGroupDescription(groupName) {
            const trailers = this.trailerStore.trailersGrouped[this.selectedMaterial]?.[groupName]
            if (!trailers || trailers.length === 0) return ''

            return trailers[0].trailer_group?.description || ''
        },
        selectTrailer(trailer) {
            this.trailerStore.selectTrailer(trailer)
            this.$router.push('/configurator')
        },
        goToMaterialSelection() {
            this.$router.push('/')
        },
        toggleGroup(groupId) {
            if (this.expandedGroups.has(groupId)) {
                this.expandedGroups.delete(groupId)
            } else {
                this.expandedGroups.add(groupId)
            }
        },
        isGroupExpanded(groupId) {
            return this.expandedGroups.has(groupId)
        }
    }
}
</script>

<style scoped lang="scss">
.main-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 20px;
    margin-bottom: 100px;
}

.logo-container {
    display: flex;
    justify-content: flex-start;
    align-items: start;
}

@media (max-width: 768px) {
    .logo-container {
        justify-content: center;
        align-items: center;
    }
}


.title {
    font-weight: 500;
    font-size: 1.5rem;
    color: $color-primary-dark;
}

.step-indicator {
    margin: 10px 0px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: default;
}

.steps {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 10px;
}

.step {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50px;
    width: 250px;
    margin-left: -10px;
    padding: 10px 20px;
    background: $color-primary-lighter;
    color: $color-text-light;
    font-weight: 500;
    clip-path: polygon(15px 50%,
            0 0,
            calc(100% - 20px) 0,
            100% 50%,
            calc(100% - 20px) 100%,
            0 100%,
            0 0);


    &.active {
        width: 350px;
        height: 60px;
        background: $color-primary;
        color: $color-white;
        clip-path: polygon(22px 50%,
                0 0,
                100% 0,
                100% 50%,
                100% 100%,
                0 100%,
                22px 50%);
    }

    @media (max-width: 678px) {
        width: 40%;
        height: 50px;
        font-size: 0.9rem;

        &.active {
            width: 60%;
            height: 55px;
            font-size: 1rem;
        }
    }
}

.selected-material-banner {
    background-color: $color-primary;
    color: $color-white;
    padding: 30px;
    border-radius: 8px;
    margin-bottom: 30px;
    text-align: left;

    h2 {
        font-size: 1.5rem;
        margin-bottom: 8px;
    }

    p {
        font-size: 1rem;
        opacity: 0.9;
        margin-bottom: 15px;
    }
}

.change-material {
    padding: 8px 20px;
    background: transparent;
    border: 1px solid $color-white;
    border-radius: 6px;
    color: $color-white;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        background: $color-white;
        color: $color-primary;
    }
}

.model-selection {
    margin-bottom: 150px;
}

.empty-state {
    text-align: center;
    padding: 40px;
    color: $color-text-light;
    background: $color-grey-light;
    border-radius: 8px;
}

.model-group {
    margin-bottom: 30px;
    border-radius: 8px;
    background: $color-white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    padding: 20px;
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
}

.group-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    padding: 10px 0;

    h2 {
        font-weight: 500;
        font-size: 1.3rem;
        margin: 0;
        color: $color-text;
    }
}

.group-description {
    color: $color-text-light;
    font-size: 0.95rem;
    line-height: 1.5;
    text-align: justify;
}

.toggle-button {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: $color-grey-light;
    color: $color-text;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: rgba($color-primary, 0.1);
        color: $color-primary;
    }
}

.group-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
    padding: 15px 0 5px 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@media (max-width: 768px) {
    .title {
        font-size: 1.8rem;
    }

    .step-indicator {
        flex-direction: column;
        align-items: center;
        gap: 10px;
    }

    .selected-material-banner {
        padding: 20px;

        h2 {
            font-size: 1.3rem;
        }
    }

    .group-container {
        grid-template-columns: 1fr;
    }

    .model-group {
        padding: 15px;
    }
}
</style>